import { Enums } from "@/services/dropdown-enums";
import { convertFirstChar } from "@/services/generic.methods";
import { getFieldKey } from "./fieldKeys";

const resolveEavValue = (entry, field) => {
  if (!entry || !field) {
    return undefined;
  }

  if (entry.textValue != null && entry.textValue !== "") {
    return entry.textValue;
  }
  if (entry.numericValue != null && entry.numericValue !== "") {
    return entry.numericValue;
  }
  if (entry.dateValue != null && entry.dateValue !== "") {
    return entry.dateValue;
  }
  if (entry.optionId != null && entry.optionId !== "") {
    return entry.optionId;
  }
  if (entry.multiOptionIds?.length) {
    return entry.multiOptionIds;
  }
  if (entry.multiOptionLabels?.length) {
    return entry.multiOptionLabels.join(", ");
  }
  return undefined;
};

const formatGridDisplayValue = (value, field) => {
  if (value == null || value === "") {
    return value;
  }

  const fieldType = field?.fieldType;
  if (fieldType === Enums.CustomFieldType.MultiValue && Array.isArray(value)) {
    const options = field?.leadCustomOptions || field?.options || [];
    const labels = options
      .filter((opt) => value.includes(opt.id))
      .map((opt) => opt.value ?? opt.label);
    return labels.join(", ");
  }

  if (
    fieldType === Enums.CustomFieldType.SingleValue &&
    field?.leadCustomOptions
  ) {
    const option = field.leadCustomOptions.find((opt) => opt.id === value);
    return option?.value ?? value;
  }

  return value;
};

/**
 * Map GraphQL customFieldValues[] to flat form keys (FieldId strings).
 */
export const mapCustomFieldValuesToForm = (
  customFieldValues = [],
  definitions = []
) => {
  const formValues = {};

  if (!customFieldValues?.length || !definitions?.length) {
    return formValues;
  }

  customFieldValues.forEach((entry) => {
    const field = definitions.find(
      (def) => String(def.id) === String(entry.fieldId)
    );
    if (!field) {
      return;
    }
    const key = getFieldKey(field);
    const value = resolveEavValue(entry, field);
    if (value !== undefined && value !== null) {
      formValues[key] = value;
    }
  });

  return formValues;
};

/**
 * Legacy fallback: read slot properties (cText1, etc.) when customFieldValues is empty.
 */
export const applyLegacySlotValuesToForm = (lead = {}, definitions = []) => {
  const formValues = {};

  definitions.forEach((field) => {
    if (field.fieldType === Enums.CustomFieldType.MultiValue) {
      return;
    }
    const legacyProp = field.mappedName
      ? convertFirstChar(field.mappedName)
      : null;
    if (!legacyProp) {
      return;
    }
    const value = lead[legacyProp];
    if (value !== undefined && value !== null) {
      formValues[getFieldKey(field)] = value;
    }
  });

  return formValues;
};

/**
 * Map form flat keys to REST customFields payload keyed by FieldId strings.
 */
export const mapFormToCustomFieldsPayload = (
  formData = {},
  definitions = []
) => {
  const payload = {};

  definitions.forEach((field) => {
    const key = getFieldKey(field);
    const value = formData[key];
    if (value !== undefined && value !== null && value !== "") {
      payload[key] = value;
    }
  });

  return payload;
};

/**
 * Map customFieldValues to grid row properties (column keys = FieldId or legacy slot).
 */
export const mapCustomFieldValuesToGridRow = (
  customFieldValues = [],
  definitions = []
) => {
  const row = {};

  if (!customFieldValues?.length || !definitions?.length) {
    return row;
  }

  customFieldValues.forEach((entry) => {
    const field = definitions.find(
      (def) => String(def.id) === String(entry.fieldId)
    );
    if (!field) {
      return;
    }
    const raw = resolveEavValue(entry, field);
    const display = formatGridDisplayValue(raw, field);
    const columnKey = getFieldKey(field);
    row[columnKey] = display;
    if (field.mappedName) {
      row[convertFirstChar(field.mappedName)] = display;
    }
  });

  return row;
};
